import type { NewDesignConfig } from "@innoclouds/api";
import {
	addToCache,
	emitter,
	InitConnection,
	THEME_HASH_KEY,
	useBadgesStore,
	useCartLibStore,
	useOrgUnitStore,
	useSyncWorker,
	useTableStore,
	useThemeStore,
} from "@innoclouds/shared";
import i18next from "i18next";
import { type App, ref, watch } from "vue";
import constants from "./constants/constants";
import getOrgUnitId from "./utils/useOrgUnitId";

const cacheMedia = (data: NewDesignConfig | null | undefined) => {
	if (!data) return;
	const mediaToCache = new Set<string | null | undefined>();
	mediaToCache.add(data.fallback_image); // Default Image

	return Promise.allSettled(Array.from(mediaToCache).filter(Boolean).map(addToCache));
};

export const connectionPlugin = {
	install(app: App) {
		InitConnection._isQrMenu = true;

		const connection = ref<any>(InitConnection.create({ ...constants.value, user: null } as any));

		i18next.on("languageChanged", () => {
			themeStore.refetch();
			getOrgUnitId(orgUnit.id);
		});

		const orgUnit = useOrgUnitStore();
		const tableStore = useTableStore();
		const badgesStore = useBadgesStore();
		const cartLibStore = useCartLibStore();
		const themeStore = useThemeStore();

		emitter.on("update_design_config", (data: any) => {
			if (data.hash_sum === localStorage.getItem("themeHash")) return;
			themeStore.refetch();
		});

		emitter.on("update_org_unit", ({ hash_sum }: any) => {
			if (orgUnit.data?.hash_sum !== hash_sum) {
				orgUnit.refetch();
				getOrgUnitId(constants.value.pointId); // @TODO: wildcard remove
			}
		});

		emitter.on("update_badges", (data: any) => {
			if (data?.hash_sum && data.hash_sum === badgesStore.hashSum) return;
			badgesStore.load();
		});

		emitter.on("update_schedule", (data: any) => {
			if (tableStore.data?.branch?.schedule?.hash_sum !== data.hash_sum) {
				tableStore.refresh();
			}
		});

		app.config.globalProperties.$connection = connection;
		app.provide("connection", connection);

		useSyncWorker({
			orgId: constants.value.pointId,
			domain: import.meta.env.VITE_API_URL,
			wsUrl: import.meta.env.VITE_WS_URL,
			cacheMedia: false,
			withCombos: false,
		});

		watch(
			() => themeStore.data,
			(data) => {
				if (!data) return;
				if (data.hash_sum !== localStorage.getItem(THEME_HASH_KEY)) cacheMedia(data);
				localStorage.setItem(THEME_HASH_KEY, data.hash_sum);
			},
			{ immediate: true, deep: true },
		);

		watch(
			constants,
			(value) => {
				cartLibStore.orderItemCommentSuggestions = value?.order_item_comment_suggestions || [];
			},
			{ immediate: true, deep: true },
		);
	},
};
